import React, { useMemo, useState } from "react";
import {
  formatMoney,
  formatNumber,
  getItemCategory,
  getItems,
  getMenuGroupKey,
  getNumber,
} from "../menuEngineeringUtils";
import { RankBadge } from "./menuDashboard";
import { isModifier } from "./menuDashboardInsights";
import MenuGroupTabs, { useMenuGroups } from "./menuGroupTabs";

const PRIORITY = { Puzzle: 0, Star: 1, Plowhorse: 2, Dog: 3, Unrated: 4 };
const CHANNELS = ["Printed menu (PDF)", "QR menu", "Kiosk", "Delivery apps"];

const combineItemsByRef = (items) => {
  const entries = [];
  const byReference = new Map();
  items.forEach((item) => {
    const reference = item.itemRef == null ? "" : String(item.itemRef).trim();
    let entry = reference ? byReference.get(reference) : null;
    if (!entry) {
      entry = {
        item,
        count: 0,
        quantity: 0,
        sales: 0,
        cost: 0,
        profit: 0,
      };
      entries.push(entry);
      if (reference) byReference.set(reference, entry);
    }
    const quantity = getNumber(item.quantity);
    entry.count += 1;
    entry.quantity += quantity;
    entry.sales +=
      item.totalSales == null
        ? getNumber(item.avgPrice) * quantity
        : getNumber(item.totalSales);
    entry.cost +=
      item.totalCost == null
        ? getNumber(item.avgCost) * quantity
        : getNumber(item.totalCost);
    entry.profit +=
      item.totalProfit == null
        ? getNumber(item.avgProfit) * quantity
        : getNumber(item.totalProfit);
  });

  return entries.map((entry) => {
    if (entry.count === 1) return entry.item;
    return {
      ...entry.item,
      quantity: entry.quantity,
      totalSales: entry.sales,
      totalCost: entry.cost,
      totalProfit: entry.profit,
      avgPrice: entry.quantity
        ? entry.sales / entry.quantity
        : entry.item.avgPrice,
      avgCost: entry.quantity
        ? entry.cost / entry.quantity
        : entry.item.avgCost,
      avgProfit: entry.quantity
        ? entry.profit / entry.quantity
        : entry.item.avgProfit,
    };
  });
};

function MenuOrder({ outlets }) {
  const [outletKey, setOutletKey] = useState("");
  const [view, setView] = useState("recommended");
  const [channels, setChannels] = useState([]);
  const [showPreviewNotice, setShowPreviewNotice] = useState(false);
  const selectedOutlet =
    outlets.find((outlet) => (outlet.chaKey || outlet.name) === outletKey) ||
    outlets[0];
  const outletItems = useMemo(
    () =>
      combineItemsByRef(
        getItems(selectedOutlet ? [selectedOutlet] : []).filter(
          (item) => !isModifier(item)
        )
      ),
    [selectedOutlet]
  );
  const { items: rankedItems, tabs } = useMenuGroups(
    outletItems,
    selectedOutlet
  );

  const groups = useMemo(() => {
    const grouped = new Map();
    rankedItems.forEach((item) => {
      const key = JSON.stringify([
        getMenuGroupKey(item, "page"),
        getMenuGroupKey(item, "category"),
      ]);
      const group = grouped.get(key) || [];
      group.push(item);
      grouped.set(key, group);
    });
    return [...grouped.entries()].map(([key, groupItems]) => {
      const currentOrder = [...groupItems].sort((a, b) =>
        String(a.itemRef ?? "").localeCompare(
          String(b.itemRef ?? ""),
          undefined,
          {
            numeric: true,
          }
        )
      );
      const recommendedOrder = [...currentOrder].sort((a, b) => {
        const rankDifference = PRIORITY[a._rank] - PRIORITY[b._rank];
        return (
          rankDifference || Number(b.avgProfit || 0) - Number(a.avgProfit || 0)
        );
      });
      return {
        key,
        name: getItemCategory(groupItems[0]),
        items: view === "recommended" ? recommendedOrder : currentOrder,
      };
    });
  }, [rankedItems, view]);

  const puzzles = rankedItems.filter((item) => item._rank === "Puzzle").length;
  const stars = rankedItems.filter((item) => item._rank === "Star").length;
  const dogs = rankedItems.filter((item) => item._rank === "Dog").length;
  const highestProfit = [...rankedItems].sort(
    (a, b) => Number(b.avgProfit || 0) - Number(a.avgProfit || 0)
  )[0];

  if (!outletItems.length) {
    return (
      <div className="menuEngineeringEmpty compact">
        <h2>No menu items to arrange</h2>
        <p>Try another date range or select a different outlet.</p>
      </div>
    );
  }

  const toggleChannel = (channel) =>
    setChannels((current) =>
      current.includes(channel)
        ? current.filter((item) => item !== channel)
        : [...current, channel]
    );

  return (
    <div className="menuOrder">
      <section className="menuOrderIntro">
        <div>
          <div className="menuCardEyebrow">MENU LAYOUT / PREVIEW</div>
          <h2>Let the matrix guide your menu order</h2>
          <p>
            Preview a suggested sequence based on menu-engineering classes.
            Review the items before applying changes to any channel.
          </p>
        </div>
        <label className="menuOrderOutlet">
          Outlet
          <select
            value={selectedOutlet?.chaKey || selectedOutlet?.name || ""}
            onChange={(event) => setOutletKey(event.target.value)}
          >
            {outlets.map((outlet, index) => {
              const key = outlet.chaKey || outlet.name || `outlet-${index}`;
              return (
                <option key={key} value={key}>
                  {outlet.name || `Outlet ${index + 1}`}
                </option>
              );
            })}
          </select>
        </label>
      </section>

      {/* <MenuGroupTabs {...tabs} /> */}
      {!rankedItems.length && (
        <div className="menuEngineeringEmpty compact">
          <h2>No menu items in this selection</h2>
          <p>Try another page or category.</p>
        </div>
      )}

      <div className="menuOrderTabs" role="group" aria-label="Menu order view">
        <button
          type="button"
          className={view === "current" ? "active" : ""}
          onClick={() => setView("current")}
          aria-pressed={view === "current"}
        >
          Current POS order
        </button>
        <button
          type="button"
          className={view === "recommended" ? "active" : ""}
          onClick={() => setView("recommended")}
          aria-pressed={view === "recommended"}
        >
          Suggested order
          <span>Preview</span>
        </button>
      </div>

      <div className="menuOrderLayout">
        <section className="menuCard menuOrderPreview">
          <div className="menuPrintedMenu">
            <div className="menuPrintedHeader">
              <strong>{selectedOutlet?.name || "MENU"}</strong>
              <span>MENU PREVIEW</span>
            </div>
            {groups.map((group) => (
              <div className="menuPrintedSection" key={group.key}>
                <h3>{group.name}</h3>
                {group.items.map((item, index) => (
                  <article
                    className={`menuPrintedItem ${item._rank === "Puzzle" && view === "recommended" ? "featured" : ""}`}
                    key={item._itemKey}
                  >
                    <div className="menuPrintedNumber">{index + 1}</div>
                    <div className="menuPrintedDetails">
                      <div>
                        <strong>{item.itemDescription || "Menu item"}</strong>
                        {index === 0 &&
                          view === "recommended" &&
                          item._rank === "Puzzle" && (
                            <span className="menuChefPick">FEATURED</span>
                          )}
                      </div>
                      <small>
                        <RankBadge rank={item._rank} />
                        {/* {item.itemRef != null && `Ref ${item.itemRef}    `} */}
                        {item.quantity != null &&
                          `${formatNumber(item.quantity)} sold`}
                      </small>
                    </div>
                    <span className="menuPrintedPrice">
                      {formatMoney(item.avgPrice)}
                    </span>
                  </article>
                ))}
              </div>
            ))}
            <div className="menuPrintedFootnote">
              Classification markers are for this preview only.
            </div>
          </div>
        </section>

        <aside className="menuCard menuOrderRationale">
          <div className="menuCardEyebrow">WHY THIS ORDER</div>
          <h2>Use performance to shape placement</h2>
          <div className="menuReasonList">
            <Reason
              number="01"
              title="Give high-margin puzzles a stronger position"
              detail={
                puzzles
                  ? `${puzzles} puzzle ${puzzles === 1 ? "item is" : "items are"} available to test in more prominent positions.`
                  : "No puzzle items were returned for this outlet in the selected period."
              }
            />
            <Reason
              number="02"
              title="Keep stars easy to find"
              detail={`${stars} star ${stars === 1 ? "item" : "items"} ${stars === 1 ? "is" : "are"} identified in this outlet’s results.`}
            />
            <Reason
              number="03"
              title="Review low-performing items"
              detail={`${dogs} dog ${dogs === 1 ? "item" : "items"} ${dogs === 1 ? "needs" : "need"} a menu or recipe review before being removed.`}
            />
            {highestProfit && (
              <Reason
                number="04"
                title="Highlight the strongest average profit"
                detail={`${highestProfit.itemDescription || "Top item"} · ${formatMoney(highestProfit.avgProfit)} average profit.`}
              />
            )}
          </div>
          <div className="menuOrderApply">
            <strong>Apply preview to</strong>
            <div className="menuChannelList">
              {CHANNELS.map((channel) => (
                <button
                  type="button"
                  className={channels.includes(channel) ? "selected" : ""}
                  key={channel}
                  onClick={() => toggleChannel(channel)}
                  aria-pressed={channels.includes(channel)}
                >
                  <span aria-hidden="true">
                    {channels.includes(channel) ? "✓" : "+"}
                  </span>
                  {channel}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="menuReviewButton"
              onClick={() => setShowPreviewNotice(true)}
            >
              Review layout
            </button>
            {showPreviewNotice && (
              <p className="menuPreviewNotice" role="status">
                This is a design preview; publishing menu changes is not
                connected yet.
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Reason({ number, title, detail }) {
  return (
    <article className="menuReason">
      <span>{number}</span>
      <div>
        <strong>{title}</strong>
        <p>{detail}</p>
      </div>
    </article>
  );
}

export default MenuOrder;
